import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { getDashboardStats, getInvoices, getClients, getStockProfitability } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { 
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  Tooltip, 
  ResponsiveContainer,
  BarChart,
  Bar
} from 'recharts';
import { 
  Plus, 
  FileText, 
  Users, 
  TrendingUp, 
  Clock,
  AlertTriangle,
  CheckCircle,
  ArrowUpRight,
  ArrowDownRight,
  Zap,
  Receipt,
  CreditCard,
  Activity,
  ChevronRight,
  Send,
  Sparkles
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { usePlanAccess } from '../hooks/usePlanAccess';

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08, delayChildren: 0.1 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0, 
    transition: { duration: 0.4, ease: "easeOut" } 
  }
};

const glowVariants = {
  initial: { opacity: 0.5 },
  animate: { 
    opacity: [0.5, 1, 0.5], 
    transition: { duration: 2, repeat: Infinity, ease: "easeInOut" } 
  }
};

const DashboardPage = () => {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentInvoices, setRecentInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [profitData, setProfitData] = useState([]);
  const { plan, trial, status } = usePlanAccess();

  const loadDashboard = useCallback(async () => {
    try {
      const [statsRes, invoicesRes, profitRes] = await Promise.all([
        getDashboardStats(),
        getInvoices(),
        getStockProfitability().catch(() => ({ data: { products: [] } }))
      ]);
      setStats(statsRes.data);
      setRecentInvoices(invoicesRes.data.slice(0, 5));
      setProfitData(profitRes.data.products || []);
    } catch (error) {
      toast.error(t('common.loading') + ' error');
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadDashboard();
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, [loadDashboard]);

  const getLocale = () => {
    switch(language) {
      case 'de': return 'de-CH';
      case 'en': return 'en-GB';
      default: return 'fr-CH';
    }
  };

  const formatCurrency = (amount, currency = 'CHF') => {
    return new Intl.NumberFormat(getLocale(), {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0) + ' ' + currency;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString(getLocale(), { day: '2-digit', month: '2-digit' });
  };

  // Use real monthly chart data from backend
  const chartData = stats?.monthly_chart || [];

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'text-[#39FF14]';
      case 'sent': return 'text-[#00F0FF]';
      case 'overdue': return 'text-[#FF0055]';
      default: return 'text-zinc-400';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'paid': return t('invoices.paid');
      case 'sent': return t('invoices.sent');
      case 'overdue': return t('invoices.overdue');
      case 'draft': return t('invoices.draft');
      default: return status;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          className="w-12 h-12 border-2 border-[#00F0FF] border-t-transparent rounded-full"
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white p-4 sm:p-6 lg:p-8" data-testid="futuristic-dashboard">
      {/* Background Grid Effect */}
      <div 
        className="fixed inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage: `linear-gradient(rgba(0,240,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(0,240,255,0.1) 1px, transparent 1px)`,
          backgroundSize: '50px 50px'
        }}
      />

      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="max-w-[1600px] mx-auto relative z-10"
      >
        {/* Header */}
        <motion.div variants={itemVariants} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <motion.div 
                variants={glowVariants}
                initial="initial"
                animate="animate"
                className="w-2 h-2 rounded-full bg-[#39FF14]"
              />
              <span className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-medium">
                {language === 'fr' ? 'Système actif' : language === 'de' ? 'System aktiv' : 'System active'}
              </span>
            </div>
            <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {t('dashboard.welcome')}, <span className="text-[#00F0FF]">{user?.company_name?.split(' ')[0] || 'User'}</span>
            </h1>
            <p className="text-zinc-500 mt-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              {t('dashboard.title')} • {currentTime.toLocaleDateString(getLocale(), { weekday: 'long', day: 'numeric', month: 'long' })}
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            <div className="text-right mr-4 hidden sm:block">
              <p className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                {currentTime.toLocaleTimeString(getLocale(), { hour: '2-digit', minute: '2-digit' })}
              </p>
              <p className="text-xs text-zinc-500 uppercase tracking-wider">{language === 'fr' ? 'Heure locale' : language === 'de' ? 'Ortszeit' : 'Local time'}</p>
            </div>
            <Button 
              onClick={() => navigate('/app/invoices/new')}
              className="bg-[#00F0FF] hover:bg-[#00F0FF]/90 text-black font-semibold px-6 rounded-sm"
              style={{ fontFamily: 'JetBrains Mono, monospace' }}
              data-testid="new-invoice-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              {t('dashboard.newInvoice').toUpperCase()}
            </Button>
          </div>
        </motion.div>

        {/* Trial Countdown Banner */}
        {status === 'trial' && user?.role !== 'admin' && (
          <motion.div
            variants={itemVariants}
            className="mb-6 relative overflow-hidden rounded-sm border border-blue-500/30"
            data-testid="trial-banner"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-blue-600/10 via-indigo-600/10 to-purple-600/10" />
            <div className="relative z-10 px-5 py-4 flex items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-sm bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-white">
                    {trial?.days_left !== undefined
                      ? (language === 'de' ? `Testphase Pro: noch ${trial.days_left} Tage`
                        : language === 'en' ? `Pro trial: ${trial.days_left} days left`
                        : `Essai Pro : ${trial.days_left} jours restants`)
                      : (language === 'de' ? 'Testphase aktiv'
                        : language === 'en' ? 'Trial active'
                        : 'Essai gratuit actif')}
                  </p>
                  <p className="text-xs text-zinc-400 mt-0.5">
                    {language === 'de' ? 'Abonnieren Sie, um alle Funktionen beizubehalten'
                      : language === 'en' ? 'Subscribe to keep all features after the trial ends'
                      : 'Abonnez-vous pour conserver toutes les fonctionnalites apres la fin de l\'essai'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                {trial?.days_left !== undefined && (
                  <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-white/5 rounded-sm border border-white/10">
                    <Clock className="w-3.5 h-3.5 text-blue-400" />
                    <span className="text-sm font-mono font-bold text-blue-400" data-testid="trial-days-count">
                      {trial.days_left}j
                    </span>
                  </div>
                )}
                <Button
                  onClick={() => navigate('/pricing')}
                  className="bg-blue-600 hover:bg-blue-700 text-white text-xs px-4 rounded-sm"
                  data-testid="trial-upgrade-btn"
                >
                  {language === 'de' ? 'Tarife ansehen' : language === 'en' ? 'View plans' : 'Voir les offres'}
                  <ChevronRight className="w-3 h-3 ml-1" />
                </Button>
              </div>
            </div>
            {/* Progress bar for days remaining */}
            {trial?.days_left !== undefined && (
              <div className="h-0.5 bg-white/5">
                <div className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 transition-all duration-500" style={{ width: `${Math.max(5, (trial.days_left / 14) * 100)}%` }} />
              </div>
            )}
          </motion.div>
        )}

        {/* Stats Grid - 6 cards */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          {/* Total Revenue */}
          <motion.div 
            variants={itemVariants}
            className="bg-[#111113] border border-white/10 rounded-sm p-5 relative overflow-hidden group hover:border-[#00F0FF]/50 transition-colors"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-[#00F0FF]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs uppercase tracking-[0.15em] text-zinc-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                  {t('dashboard.totalRevenue')}
                </span>
                <TrendingUp className="w-4 h-4 text-[#39FF14]" />
              </div>
              <p className="text-3xl font-bold tracking-tight group-hover:text-[#00F0FF] transition-colors" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                {formatCurrency(stats?.total_revenue)}
              </p>
              <div className="flex items-center gap-1 mt-2">
                <ArrowUpRight className="w-3 h-3 text-[#39FF14]" />
                <span className="text-xs text-[#39FF14]">+12.5%</span>
                <span className="text-xs text-zinc-500 ml-1">{t('dashboard.thisMonth')}</span>
              </div>
            </div>
          </motion.div>

          {/* Pending Amount */}
          <motion.div 
            variants={itemVariants}
            className="bg-[#111113] border border-white/10 rounded-sm p-5 relative overflow-hidden group hover:border-[#FF0055]/50 transition-colors"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-[#FF0055]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs uppercase tracking-[0.15em] text-zinc-500">{t('dashboard.pendingAmount')}</span>
                <Clock className="w-4 h-4 text-[#FF0055]" />
              </div>
              <p className="text-3xl font-bold tracking-tight group-hover:text-[#FF0055] transition-colors" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                {formatCurrency(stats?.pending_amount)}
              </p>
              <div className="flex items-center gap-1 mt-2">
                <span className="text-xs text-zinc-500">{stats?.pending_invoices || 0} {t('nav.invoices').toLowerCase()}</span>
              </div>
            </div>
          </motion.div>

          {/* Total Invoices */}
          <motion.div 
            variants={itemVariants}
            className="bg-[#111113] border border-white/10 rounded-sm p-5 relative overflow-hidden group hover:border-[#00F0FF]/50 transition-colors"
          >
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs uppercase tracking-[0.15em] text-zinc-500">{t('nav.invoices')}</span>
                <Receipt className="w-4 h-4 text-[#00F0FF]" />
              </div>
              <p className="text-3xl font-bold tracking-tight group-hover:text-[#00F0FF] transition-colors" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                {stats?.total_invoices || 0}
              </p>
              <div className="flex items-center gap-2 mt-2">
                <span className="text-xs text-[#39FF14]">{stats?.paid_invoices || 0} {t('invoices.paid').toLowerCase()}</span>
                <span className="text-xs text-zinc-500">•</span>
                <span className="text-xs text-zinc-500">{stats?.draft_invoices || 0} {t('invoices.draft').toLowerCase()}</span>
              </div>
            </div>
          </motion.div>

          {/* Clients */}
          <motion.div 
            variants={itemVariants}
            className="bg-[#111113] border border-white/10 rounded-sm p-5 relative overflow-hidden group hover:border-[#00F0FF]/50 transition-colors"
          >
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs uppercase tracking-[0.15em] text-zinc-500">{t('nav.clients')}</span>
                <Users className="w-4 h-4 text-[#00F0FF]" />
              </div>
              <p className="text-3xl font-bold tracking-tight group-hover:text-[#00F0FF] transition-colors" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                {stats?.total_clients || 0}
              </p>
            </div>
          </motion.div>

          {/* Total Expenses */}
          <motion.div 
            variants={itemVariants}
            className="bg-[#111113] border border-white/10 rounded-sm p-5 relative overflow-hidden group hover:border-[#FF6600]/50 transition-colors"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-[#FF6600]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs uppercase tracking-[0.15em] text-zinc-500">{language === 'fr' ? 'Depenses' : language === 'de' ? 'Ausgaben' : 'Expenses'}</span>
                <ArrowDownRight className="w-4 h-4 text-[#FF6600]" />
              </div>
              <p className="text-3xl font-bold tracking-tight group-hover:text-[#FF6600] transition-colors" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                {formatCurrency(stats?.total_expenses)}
              </p>
              <span className="text-xs text-zinc-500">{new Date().getFullYear()}</span>
            </div>
          </motion.div>

          {/* Net Profit */}
          <motion.div 
            variants={itemVariants}
            className="bg-[#111113] border border-white/10 rounded-sm p-5 relative overflow-hidden group hover:border-[#39FF14]/50 transition-colors"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-[#39FF14]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs uppercase tracking-[0.15em] text-zinc-500">{language === 'fr' ? 'Benefice net' : language === 'de' ? 'Nettogewinn' : 'Net profit'}</span>
                <Activity className="w-4 h-4 text-[#39FF14]" />
              </div>
              <p className={`text-3xl font-bold tracking-tight transition-colors ${(stats?.net_profit || 0) >= 0 ? 'text-[#39FF14] group-hover:text-[#39FF14]' : 'text-[#FF0055] group-hover:text-[#FF0055]'}`} style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                {(stats?.net_profit || 0) >= 0 ? '+' : ''}{formatCurrency(stats?.net_profit)}
              </p>
              {stats?.bank_balance != null && (
                <span className="text-xs text-zinc-500">{language === 'fr' ? 'Banque' : 'Bank'}: {formatCurrency(stats.bank_balance)}</span>
              )}
            </div>
          </motion.div>
        </div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Revenue Chart */}
          <motion.div 
            variants={itemVariants}
            className="lg:col-span-2 bg-[#111113] border border-white/10 rounded-sm p-5"
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-lg font-semibold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {language === 'fr' ? 'Revenus vs Depenses' : language === 'de' ? 'Umsatz vs Ausgaben' : 'Revenue vs Expenses'}
                </h2>
                <p className="text-xs text-zinc-500 mt-1">{language === 'fr' ? '6 derniers mois' : language === 'de' ? 'Letzte 6 Monate' : 'Last 6 months'}</p>
              </div>
              <div className="flex items-center gap-4 text-xs">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-[#00F0FF]" />
                  <span className="text-zinc-400">{t('fiscal.revenue')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-[#FF6600]" />
                  <span className="text-zinc-400">{language === 'fr' ? 'Depenses' : language === 'de' ? 'Ausgaben' : 'Expenses'}</span>
                </div>
              </div>
            </div>
            <div className="h-[250px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#00F0FF" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#00F0FF" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorExpenses" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#FF6600" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#FF6600" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <XAxis 
                    dataKey="month" 
                    axisLine={false} 
                    tickLine={false}
                    tick={{ fill: '#71717A', fontSize: 12, fontFamily: 'JetBrains Mono' }}
                  />
                  <YAxis 
                    axisLine={false} 
                    tickLine={false}
                    tick={{ fill: '#71717A', fontSize: 12, fontFamily: 'JetBrains Mono' }}
                    tickFormatter={(v) => v >= 1000 ? `${v/1000}k` : v}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#111113', 
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '2px',
                      fontFamily: 'JetBrains Mono'
                    }}
                    labelStyle={{ color: '#A1A1AA' }}
                    formatter={(value, name) => [`${value.toLocaleString()} CHF`, name === 'revenue' ? t('fiscal.revenue') : name === 'expenses' ? (language === 'fr' ? 'Depenses' : 'Expenses') : 'Profit']}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="revenue" 
                    stroke="#00F0FF" 
                    strokeWidth={2}
                    fillOpacity={1} 
                    fill="url(#colorRevenue)" 
                  />
                  <Area 
                    type="monotone" 
                    dataKey="expenses" 
                    stroke="#FF6600" 
                    strokeWidth={2}
                    fillOpacity={1} 
                    fill="url(#colorExpenses)" 
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </motion.div>

          {/* Product Profitability Chart */}
          {profitData.length > 0 && (
            <motion.div 
              variants={itemVariants}
              className="bg-[#111113] border border-white/10 rounded-sm p-5"
              data-testid="profitability-chart"
            >
              <h2 className="text-lg font-semibold tracking-tight mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {language === 'fr' ? 'Rentabilite par produit' : language === 'de' ? 'Rentabilitaet pro Produkt' : 'Profitability per product'}
              </h2>
              <div className="h-[250px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={profitData} layout="vertical" margin={{ left: 10, right: 20, top: 5, bottom: 5 }}>
                    <XAxis type="number" stroke="#525252" tick={{ fill: '#a1a1aa', fontSize: 11, fontFamily: 'JetBrains Mono' }} tickFormatter={(v) => `${v}`} />
                    <YAxis type="category" dataKey="name" stroke="#525252" tick={{ fill: '#d4d4d8', fontSize: 11 }} width={120} />
                    <Tooltip 
                      contentStyle={{ background: '#18181b', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 4, fontFamily: 'JetBrains Mono', fontSize: 12 }}
                      itemStyle={{ color: '#d4d4d8' }}
                      formatter={(value, name) => [
                        `${Number(value).toFixed(2)} CHF`,
                        name === 'purchases' ? (language === 'fr' ? 'Achats' : 'Purchases') :
                        name === 'sales' ? (language === 'fr' ? 'Ventes' : 'Sales') :
                        (language === 'fr' ? 'Benefice' : 'Profit')
                      ]}
                    />
                    <Bar dataKey="purchases" fill="#ef4444" radius={[0, 2, 2, 0]} name="purchases" barSize={14} />
                    <Bar dataKey="sales" fill="#10b981" radius={[0, 2, 2, 0]} name="sales" barSize={14} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              {/* Top/Worst product */}
              <div className="flex gap-4 mt-3 text-xs">
                {profitData[0] && profitData[0].profit > 0 && (
                  <div className="flex items-center gap-1.5 text-emerald-400">
                    <ArrowUpRight className="w-3.5 h-3.5" />
                    <span style={{ fontFamily: 'JetBrains Mono' }}>Top: {profitData[0].name} (+{profitData[0].profit.toFixed(0)} CHF)</span>
                  </div>
                )}
                {profitData.length > 1 && profitData[profitData.length - 1].profit < 0 && (
                  <div className="flex items-center gap-1.5 text-red-400">
                    <ArrowDownRight className="w-3.5 h-3.5" />
                    <span style={{ fontFamily: 'JetBrains Mono' }}>Bas: {profitData[profitData.length - 1].name} ({profitData[profitData.length - 1].profit.toFixed(0)} CHF)</span>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {/* Quick Actions */}
          <motion.div 
            variants={itemVariants}
            className="bg-[#111113] border border-white/10 rounded-sm p-5"
          >
            <h2 className="text-lg font-semibold tracking-tight mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {t('dashboard.quickActions')}
            </h2>
            <div className="space-y-3">
              <button
                onClick={() => navigate('/app/invoices/new')}
                className="w-full flex items-center justify-between p-4 bg-black/50 border border-white/10 rounded-sm hover:border-[#00F0FF]/50 hover:bg-[#00F0FF]/5 transition-all group"
                data-testid="quick-new-invoice"
              >
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-[#00F0FF]" />
                  <span className="text-sm font-medium" style={{ fontFamily: 'JetBrains Mono' }}>{t('dashboard.newInvoice')}</span>
                </div>
                <ChevronRight className="w-4 h-4 text-zinc-500 group-hover:text-[#00F0FF] transition-colors" />
              </button>
              
              <button
                onClick={() => navigate('/app/quotes/new')}
                className="w-full flex items-center justify-between p-4 bg-black/50 border border-white/10 rounded-sm hover:border-[#00F0FF]/50 hover:bg-[#00F0FF]/5 transition-all group"
                data-testid="quick-new-quote"
              >
                <div className="flex items-center gap-3">
                  <Send className="w-5 h-5 text-[#00F0FF]" />
                  <span className="text-sm font-medium" style={{ fontFamily: 'JetBrains Mono' }}>{t('dashboard.newQuote')}</span>
                </div>
                <ChevronRight className="w-4 h-4 text-zinc-500 group-hover:text-[#00F0FF] transition-colors" />
              </button>
              
              <button
                onClick={() => navigate('/app/clients')}
                className="w-full flex items-center justify-between p-4 bg-black/50 border border-white/10 rounded-sm hover:border-[#00F0FF]/50 hover:bg-[#00F0FF]/5 transition-all group"
                data-testid="quick-clients"
              >
                <div className="flex items-center gap-3">
                  <Users className="w-5 h-5 text-[#00F0FF]" />
                  <span className="text-sm font-medium" style={{ fontFamily: 'JetBrains Mono' }}>{language === 'fr' ? 'Gérer clients' : language === 'de' ? 'Kunden verwalten' : 'Manage clients'}</span>
                </div>
                <ChevronRight className="w-4 h-4 text-zinc-500 group-hover:text-[#00F0FF] transition-colors" />
              </button>

              <button
                onClick={() => navigate('/app/settings')}
                className="w-full flex items-center justify-between p-4 bg-black/50 border border-white/10 rounded-sm hover:border-[#00F0FF]/50 hover:bg-[#00F0FF]/5 transition-all group"
                data-testid="quick-settings"
              >
                <div className="flex items-center gap-3">
                  <Zap className="w-5 h-5 text-[#00F0FF]" />
                  <span className="text-sm font-medium" style={{ fontFamily: 'JetBrains Mono' }}>{t('nav.settings')}</span>
                </div>
                <ChevronRight className="w-4 h-4 text-zinc-500 group-hover:text-[#00F0FF] transition-colors" />
              </button>
            </div>

            {/* Alerts */}
            {stats?.overdue_invoices > 0 && (
              <div className="mt-4 p-4 bg-[#FF0055]/10 border border-[#FF0055]/30 rounded-sm">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="w-5 h-5 text-[#FF0055]" />
                  <div>
                    <p className="text-sm font-medium text-[#FF0055]">{stats.overdue_invoices} {t('dashboard.overdueInvoices').toLowerCase()}</p>
                    <p className="text-xs text-zinc-400 mt-0.5">{formatCurrency(stats?.overdue_amount)}</p>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        </div>

        {/* Recent Invoices */}
        <motion.div 
          variants={itemVariants}
          className="mt-6 bg-[#111113] border border-white/10 rounded-sm"
        >
          <div className="flex items-center justify-between p-5 border-b border-white/10">
            <h2 className="text-lg font-semibold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {t('dashboard.recentInvoices')}
            </h2>
            <Button 
              variant="ghost" 
              className="text-[#00F0FF] hover:text-[#00F0FF] hover:bg-[#00F0FF]/10 text-xs uppercase tracking-wider"
              onClick={() => navigate('/app/invoices')}
              style={{ fontFamily: 'JetBrains Mono' }}
            >
              {t('dashboard.viewAll')}
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
          
          <div className="divide-y divide-white/5">
            {recentInvoices.length === 0 ? (
              <div className="p-8 text-center">
                <FileText className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
                <p className="text-zinc-500">{t('invoices.noInvoices')}</p>
                <Button 
                  className="mt-4 bg-[#00F0FF] hover:bg-[#00F0FF]/90 text-black"
                  onClick={() => navigate('/app/invoices/new')}
                >
                  {t('invoices.createFirst')}
                </Button>
              </div>
            ) : (
              recentInvoices.map((invoice, index) => (
                <motion.div
                  key={invoice.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="flex items-center justify-between p-4 hover:bg-white/[0.02] transition-colors cursor-pointer"
                  onClick={() => navigate(`/app/invoices/${invoice.id}/edit`)}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-2 h-2 rounded-full ${
                      invoice.status === 'paid' ? 'bg-[#39FF14]' : 
                      invoice.status === 'overdue' ? 'bg-[#FF0055]' : 
                      invoice.status === 'sent' ? 'bg-[#00F0FF]' : 'bg-zinc-500'
                    }`} />
                    <div>
                      <p className="font-medium text-sm" style={{ fontFamily: 'JetBrains Mono' }}>
                        {invoice.invoice_number}
                      </p>
                      <p className="text-xs text-zinc-500" style={{ fontFamily: 'IBM Plex Sans' }}>
                        {invoice.client?.name || (language === 'fr' ? 'Client inconnu' : language === 'de' ? 'Unbekannter Kunde' : 'Unknown client')}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-6">
                    <span className={`text-xs uppercase tracking-wider px-2 py-1 border rounded-sm ${
                      invoice.status === 'paid' ? 'border-[#39FF14]/30 text-[#39FF14]' : 
                      invoice.status === 'overdue' ? 'border-[#FF0055]/30 text-[#FF0055]' : 
                      invoice.status === 'sent' ? 'border-[#00F0FF]/30 text-[#00F0FF]' : 
                      'border-zinc-700 text-zinc-400'
                    }`} style={{ fontFamily: 'JetBrains Mono' }}>
                      {getStatusLabel(invoice.status)}
                    </span>
                    <span className="text-sm font-semibold text-right min-w-[100px]" style={{ fontFamily: 'JetBrains Mono' }}>
                      {formatCurrency(invoice.total, invoice.currency)}
                    </span>
                    <span className="text-xs text-zinc-500 min-w-[60px] text-right" style={{ fontFamily: 'JetBrains Mono' }}>
                      {formatDate(invoice.created_at)}
                    </span>
                  </div>
                </motion.div>
              ))
            )}
          </div>
        </motion.div>

        {/* Footer Stats */}
        <motion.div 
          variants={itemVariants}
          className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4"
        >
          <div className="bg-[#111113] border border-white/10 rounded-sm p-4 text-center">
            <p className="text-2xl font-bold text-[#39FF14]" style={{ fontFamily: 'JetBrains Mono' }}>
              {stats?.paid_invoices || 0}
            </p>
            <p className="text-xs text-zinc-500 uppercase tracking-wider mt-1">Payées</p>
          </div>
          <div className="bg-[#111113] border border-white/10 rounded-sm p-4 text-center">
            <p className="text-2xl font-bold text-[#00F0FF]" style={{ fontFamily: 'JetBrains Mono' }}>
              {stats?.sent_invoices || 0}
            </p>
            <p className="text-xs text-zinc-500 uppercase tracking-wider mt-1">Envoyées</p>
          </div>
          <div className="bg-[#111113] border border-white/10 rounded-sm p-4 text-center">
            <p className="text-2xl font-bold text-[#FF0055]" style={{ fontFamily: 'JetBrains Mono' }}>
              {stats?.overdue_invoices || 0}
            </p>
            <p className="text-xs text-zinc-500 uppercase tracking-wider mt-1">En retard</p>
          </div>
          <div className="bg-[#111113] border border-white/10 rounded-sm p-4 text-center">
            <p className="text-2xl font-bold" style={{ fontFamily: 'JetBrains Mono' }}>
              {formatCurrency(stats?.vat_collected)}
            </p>
            <p className="text-xs text-zinc-500 uppercase tracking-wider mt-1">TVA collectée</p>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default DashboardPage;
